import React, { ComponentType } from "react"
import { NodeProps } from "reactflow"
import { Grid } from "@material-ui/core"

import { BlockNodeData } from "@/protocols/chatBotFlow"

import useDidMount from "@/hooks/useDidMount"
import useSuperlogicaApi from "@/hooks/useSuperlogicaApi"
import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"
import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySectionGroup from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionGroup"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"
import AfterActionConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/AfterActionConditionSection"
import ContrastChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ContrastChip"
import PluginStatusReport from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/PluginStatusReport"

import SearchSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/SuperlogicaLookupContactBlock/SearchSectionEditor"
import ResponseMappingSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/SuperlogicaLookupContactBlock/ResponseMappingSectionEditor"
import ContactNotFoundConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/SuperlogicaLookupContactBlock/ContactNotFoundConditionSection"
import RequestErrorConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/SuperlogicaLookupContactBlock/RequestErrorConditionSection"
import { formatSaveInputLocationOptions, getSuperlogicaFieldLabel } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import { colors } from "@/styles/colors"

const SuperlogicaLookupContactBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()
	const isPreview = chatBotFlowConstructorStore.isPreview
	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })

	const {
		getPluginSettings,
		validatePlugin,
		getValidationContent
	} = useSuperlogicaApi()

	const pluginStatusContent = getValidationContent()

	useDidMount(async () => {
		await getPluginSettings()
	})

	const responseMapping = chatBotFlowBlockDetails.content?.superlogicaBlock?.responseMapping || []

	const customFieldOptions = formatSaveInputLocationOptions(chatBotFlowConstructorStore.constructionResources)
		.filter(option => option.type === "custom-field")

	const searchCustomFieldId = chatBotFlowBlockDetails.content?.superlogicaBlock?.search?.find(item => item.field === "condominiumName")?.customFieldId
	const searchFieldOption = customFieldOptions.find(option => option.customFieldId === searchCustomFieldId)
	const searchFieldName = searchFieldOption?.name

	const getSavedDataSummary = () => {
		if (responseMapping.length === 0) {
			return null
		}

		return (
			<Grid container direction="column" spacing={1}>
				{responseMapping.map(mapping => {
					const superlogicaFieldLabel = getSuperlogicaFieldLabel(mapping.field)
					const customFieldOption = customFieldOptions.find(option => option.customFieldId === mapping.customFieldId)
					const customFieldLabel = customFieldOption?.name || "campo não definido"

					return (
						<Grid container direction="row" wrap="nowrap" key={mapping.field} spacing={1}>
							<Grid item>
								<ContrastChip
									backgroundColor={colors.gray[100]}
									style={{
										maxWidth: "16ch",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
									title={superlogicaFieldLabel}
								/>
							</Grid>
							<Grid item xs zeroMinWidth>
								<ContrastChip
									backgroundColor={colors.blue[100]}
									style={{
										maxWidth: "15ch",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
									title={customFieldLabel}
								/>
							</Grid>
						</Grid>
					)
				})}
			</Grid>
		)
	}

	return (
		<BaseBlock
			node={node}
		>
			<BlockBodySectionGroup>
				{ !isPreview && (
					<BlockBodySection
						fullWidth
					>
						<PluginStatusReport
							pluginStatusContent={pluginStatusContent}
							onUpdate={async () => {
								await validatePlugin()
							}}
						/>
					</BlockBodySection>
				)}

				<BlockBodySection
					fullWidth
				>
					<SearchSectionEditor
						onSave={content => chatBotFlowBlockDetails.changeContent(content)}
						chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					>
						<BlockBodySectionContainer
							text={searchFieldName || ""}
							placeholder="Nenhum campo selecionado"
							title="Nome do condomínio:"
							chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
						/>
					</SearchSectionEditor>
				</BlockBodySection>

				<BlockBodySection
					fullWidth
				>
					<ResponseMappingSectionEditor
						onSave={content => chatBotFlowBlockDetails.changeContent(content)}
						chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					>
						<BlockBodySectionContainer
							text={getSavedDataSummary() || ""}
							placeholder="Nenhuma associação configurada"
							title="Salvar informação em:"
							chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
						/>
					</ResponseMappingSectionEditor>
				</BlockBodySection>
			</BlockBodySectionGroup>

			<BlockBodySectionGroup>
				<AfterActionConditionSection
					nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				/>

				<ContactNotFoundConditionSection
					nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
					text="Cadastro não encontrado"
				/>

				<RequestErrorConditionSection
					nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
					text="Houve um erro na api"
				/>
			</BlockBodySectionGroup>
		</BaseBlock>
	)
}

export default SuperlogicaLookupContactBlock
